import { cn } from '@/lib/utils';
import { useEffect, useState } from 'react';
import { CLU_COLOURS, type CluColours } from './clu-colours';
import type { CluMove } from './clu-moves';
import { CluSalute } from './clu-salute.component';
import { CluBody } from './copilot-icon.component';

/** How long the ponder takes to ease back to rest (see `.clu-ponder-out`). */
const PONDER_EXIT_MS = 300;

/** Clu pondering while he works; the thought dots reach past the box. */
export function CluPonder({
  colours = CLU_COLOURS,
  leaving = false,
  className,
}: {
  colours?: CluColours;
  /** Easing back to rest, before the still mark takes over. */
  leaving?: boolean;
  className?: string;
}) {
  return (
    <svg
      viewBox='0 0 511 511'
      fill='none'
      aria-hidden='true'
      focusable='false'
      data-clu-ponder
      className={cn(
        'shrink-0 overflow-visible select-none',
        leaving && 'clu-ponder-out',
        className
      )}
    >
      <g className='clu-ponder-bob'>
        <g transform='translate(255.5 486)'>
          <g className='clu-ponder-body'>
            <g transform='translate(-255.5 -486)'>
              <CluBody colours={colours} />
              <g className='clu-ponder-face'>
                <ellipse cx='258' cy='219.5' rx='119' ry='96.5' fill='#fff' />
                <g className='clu-ponder-look'>
                  <g transform='translate(212 214.5)'>
                    <g className='clu-ponder-blink'>
                      <circle r='25' fill={colours.eye} />
                    </g>
                  </g>
                  <g transform='translate(322 199)'>
                    <g className='clu-ponder-blink'>
                      <circle r='25' fill={colours.eye} />
                    </g>
                  </g>
                </g>
                <path
                  d='M259,225 C272,217 301,218 311,233 C322,251 311,280 279,298 C276,275 270,248 259,225 Z'
                  fill={colours.beak}
                />
              </g>
              <g transform='translate(397 392) scale(-1 1) rotate(-33)'>
                <g className='clu-ponder-wingF'>
                  <path
                    d='M0,-46 C62,-86 150,-106 194,-78 C222,-59 214,-14 176,9 C132,36 62,46 0,46 Z'
                    fill={colours.beak}
                  />
                </g>
              </g>
            </g>
          </g>
        </g>
        <g transform='translate(430 10)'>
          <g className='clu-ponder-dot1'>
            <circle r='16' fill={colours.disc} />
          </g>
        </g>
        <g transform='translate(478 -36)'>
          <g className='clu-ponder-dot2'>
            <circle r='22' fill={colours.disc} />
          </g>
        </g>
        <g transform='translate(536 -84)'>
          <g className='clu-ponder-dot3'>
            <circle r='29' fill={colours.disc} />
          </g>
        </g>
      </g>
    </svg>
  );
}

/** A pondering greeting: the 0.4s settle plus one 3.6s loop. */
const PONDER_ONCE_MS = 4000;

/**
 * Clu's mark: ponders while `thinking`, otherwise plays `move` on each
 * `waveCount` increase. A pondering move holds the ponder for one loop.
 */
export function CluLiveMark({
  thinking = false,
  waveCount = 0,
  move = 'salute',
  colours = CLU_COLOURS,
  className,
}: {
  thinking?: boolean;
  waveCount?: number;
  move?: CluMove;
  colours?: CluColours;
  className?: string;
}) {
  const [cuesSeen, setCuesSeen] = useState(waveCount);
  const [ponderOnce, setPonderOnce] = useState(false);
  if (waveCount !== cuesSeen) {
    setCuesSeen(waveCount);
    if (move === 'pondering') setPonderOnce(true);
  }

  useEffect(() => {
    if (!ponderOnce) return;
    const timer = window.setTimeout(() => setPonderOnce(false), PONDER_ONCE_MS);
    return () => window.clearTimeout(timer);
  }, [ponderOnce]);

  const pondering = thinking || ponderOnce;
  const [wasPondering, setWasPondering] = useState(pondering);
  const [leaving, setLeaving] = useState(false);
  if (pondering !== wasPondering) {
    setWasPondering(pondering);
    setLeaving(!pondering);
  }

  useEffect(() => {
    if (!leaving) return;
    const timer = window.setTimeout(() => setLeaving(false), PONDER_EXIT_MS);
    return () => window.clearTimeout(timer);
  }, [leaving]);

  return pondering || leaving ? (
    <CluPonder colours={colours} leaving={leaving} className={className} />
  ) : (
    <CluSalute
      colours={colours}
      waveCount={waveCount}
      move={move === 'pondering' ? undefined : move}
      className={className}
    />
  );
}
